import { z } from "zod";
const Count = z.number().int().nonnegative();
const Text = z.string().max(1000);
const Bot = z.object({ key: z.enum(["academy", "salebot"]), username: Text, title: Text });
/**
 * One answer per recipient row of a campaign (Mini App `recipientOutcome`, 15.10.2026): the parts add up to every row of
 * the campaign. Optional everywhere: a Mini App before its release of 15.10 does not send them. Ден 15.10: people known
 * beforehand to have blocked, deleted or never started the bot count in those reasons (`knownBefore` is the part of each
 * that was known before sending, not an addition), and `excluded` is only «did not fit the audience». The first version
 * of the contract named it `excluded_other` and sent the known ones apart as `excluded_known`: both are still read.
 */
export const BOT_CAMPAIGN_OUTCOMES = ["delivered", "blocked", "deleted", "never_started", "excluded", "excluded_known", "failed", "pending"];
const legacyExcluded = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value))
        return value;
    const { excluded_other: other, ...rest } = value;
    return "excluded" in rest || other === undefined ? rest : { ...rest, excluded: other };
};
export const BotCampaignOutcomesSchema = z.preprocess(legacyExcluded, z.object({
    delivered: Count, blocked: Count, deleted: Count, never_started: Count, excluded: Count, failed: Count, pending: Count,
    excluded_known: Count.optional(),
    knownBefore: z.object({ blocked: Count, deleted: Count, never_started: Count }).optional(),
}));
/** Each bot's base now: canWrite = reachable + unknown; canWrite + blocked + deleted + neverStarted + unsubscribed = total. */
export const BotBaseSchema = z.object({
    key: Text, username: Text, title: Text, total: Count, canWrite: Count, blocked: Count, deleted: Count, neverStarted: Count,
    unsubscribed: Count, unknown: Count, observedAt: Text.nullable(),
});
export const BotCampaignSummarySchema = z.object({
    id: z.string().regex(/^c[a-z0-9]{20,30}$/), name: Text, stage: Text, status: Text,
    audience: Text.nullable(), createdAt: Text, scheduledFor: Text.nullable(), completedAt: Text.nullable(),
    testPassed: z.boolean(), bots: z.array(Bot).min(1).max(2), coreSegment: Text.nullable(), uniquePeople: Count,
    coverage: z.object({ totalRoutes: Count, unlinkedPeople: Count, unlinkedExcludedPeople: Count }).optional(),
    audienceRules: z.array(Bot.extend({ coreSegment: Text, segmentTitle: Text, includeUnlinked: z.boolean(), linkedQueued: Count.optional(), unlinkedQueued: Count.optional() })).max(2).optional(),
    counts: z.object({ matched: Count, queued: Count, sent: Count, notDelivered: Count, pending: Count }),
    outcomes: BotCampaignOutcomesSchema.optional(),
});
export const BotCampaignListSchema = z.object({ campaigns: z.array(BotCampaignSummarySchema).max(120), bases: z.array(BotBaseSchema).max(10).optional(), now: Text });
export const BotCampaignDetailSchema = BotCampaignSummarySchema.extend({
    message: z.object({ text: z.string().max(20000), format: z.enum(["plain", "html"]),
        button: z.object({ label: Text, url: z.string().url() }).nullable(),
        photo: z.object({ mimeType: Text, fileName: Text }).nullable(), personalized: z.boolean() }),
    report: z.object({
        clickTracking: z.boolean(), linkedPeopleSent: Count, unlinkedPeopleSent: Count,
        uniquePeopleSent: Count, uniquePeopleClicked: Count, totalClicks: Count, buyersAfterSend: Count.nullable(), coreAsOf: Text.nullable(), purchaseNote: Text,
        byBot: z.array(Bot.extend({ matched: Count, queued: Count, sent: Count, failed: Count, skipped: Count, uniquePeopleSent: Count, uniquePeopleClicked: Count, totalClicks: Count, buyersAfterSend: Count.nullable(), outcomes: BotCampaignOutcomesSchema.optional() })).max(2),
        notDelivered: z.array(z.object({ key: Text, count: Count, label: Text, phase: z.enum(["before", "during"]) })),
    }),
    test: z.object({ passed: z.boolean(), recipients: z.array(z.object({ name: Text, botKey: Text, botUsername: Text, status: Text, error: Text.nullable() })).max(4) }),
});
/** A subscriber's address in a bot (Mini App `GET /api/ops/campaigns/people`); null — the person is not in that bot's base. */
export const BOT_ADDRESS_STATUSES = ["reachable", "unknown", "unsubscribed", "blocked", "deleted", "never_started"];
export const BotAddressStatusSchema = z.enum(BOT_ADDRESS_STATUSES);
const TelegramId = z.string().regex(/^\d{1,20}$/u);
export const BotPersonDeliverySchema = z.object({
    telegramId: TelegramId,
    bots: z.array(z.object({
        key: Text, username: Text, status: BotAddressStatusSchema.nullable(), observedAt: Text.nullable(),
        campaigns: z.array(z.object({ id: Text, name: Text, outcome: z.preprocess((value) => value === "excluded_other" ? "excluded" : value, z.enum(BOT_CAMPAIGN_OUTCOMES)), reason: Text.nullable(), at: Text.nullable() })).max(20),
    })).max(5),
});
export const OpsBotPeopleDeliverySchema = z.object({ people: z.array(BotPersonDeliverySchema).max(200), now: Text });
/** `GET /api/deals/:dealId/bot-delivery`: the deal card's bots. no_telegram — no Telegram ID on the deal's contact cards. */
export const DealBotDeliverySchema = z.object({
    state: z.enum(["ok", "no_telegram", "unavailable"]),
    people: z.array(BotPersonDeliverySchema).max(20),
    checkedAt: Text,
});
//# sourceMappingURL=bot-campaigns.js.map